// src/components/ui.jsx
import { useTimer } from "../hooks/index.js";
import { VOTE_DURATION } from "../utils/constants.js";

// ══════════════════════════════════════════════════════════════
// Design tokens (inline — no CSS-in-JS lib needed)
// ══════════════════════════════════════════════════════════════
export const T = {
  screen: {
    minHeight: "100vh",
    padding: "2rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    animation: "fadeInUp 0.5s ease",
  },
  screenTop: {
    minHeight: "100vh",
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    animation: "fadeInUp 0.4s ease",
  },
  logo: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "clamp(2.5rem, 8vw, 5rem)",
    fontWeight: 900,
    letterSpacing: "0.3em",
    color: "var(--gold)",
    textShadow: "0 0 40px rgba(201,168,76,0.3)",
    marginBottom: "0.15em",
    textAlign: "center",
  },
  logoSub: {
    fontFamily: "'Courier Prime', monospace",
    fontSize: "clamp(0.55rem, 1.5vw, 0.75rem)",
    letterSpacing: "0.45em",
    color: "var(--paper2)",
    opacity: 0.6,
    marginBottom: "2.5rem",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  card: {
    background: "rgba(242,234,216,0.05)",
    border: "1px solid rgba(201,168,76,0.2)",
    borderRadius: 3,
    padding: "2rem",
    width: "100%",
    maxWidth: 480,
    backdropFilter: "blur(4px)",
    position: "relative",
  },
  cardTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "1.2rem",
    color: "var(--gold)",
    marginBottom: "1.5rem",
    paddingBottom: "0.6rem",
    borderBottom: "1px solid rgba(201,168,76,0.2)",
    letterSpacing: "0.05em",
  },
  sectionLabel: {
    display: "block",
    fontSize: "0.6rem",
    letterSpacing: "0.2em",
    color: "var(--gold-dim)",
    marginBottom: "0.4rem",
    textTransform: "uppercase",
  },
};

// ══════════════════════════════════════════════════════════════
// LABEL
// ══════════════════════════════════════════════════════════════
export function Label({ children, style }) {
  return (
    <label style={{ ...T.sectionLabel, ...style }}>{children}</label>
  );
}

// ══════════════════════════════════════════════════════════════
// INPUT
// ══════════════════════════════════════════════════════════════
export function Input({ style, ...props }) {
  return (
    <input
      style={{
        width: "100%",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(201,168,76,0.25)",
        borderRadius: 2,
        padding: "0.75rem 1rem",
        color: "var(--paper)",
        fontFamily: "'Courier Prime', monospace",
        fontSize: "1rem",
        outline: "none",
        ...style,
      }}
      {...props}
    />
  );
}

// ══════════════════════════════════════════════════════════════
// SELECT
// ══════════════════════════════════════════════════════════════
export function Select({ style, children, ...props }) {
  return (
    <select
      style={{
        width: "100%",
        background: "#1a130d",
        border: "1px solid rgba(201,168,76,0.25)",
        borderRadius: 2,
        padding: "0.75rem 1rem",
        color: "var(--paper)",
        fontFamily: "'Courier Prime', monospace",
        fontSize: "1rem",
        outline: "none",
        cursor: "pointer",
        ...style,
      }}
      {...props}
    >
      {children}
    </select>
  );
}

// ══════════════════════════════════════════════════════════════
// BUTTON
// ══════════════════════════════════════════════════════════════
const BTN_VARIANTS = {
  gold:   { background: "var(--gold)",          color: "var(--ink)",          fontWeight: 700 },
  ghost:  { background: "transparent",          color: "var(--gold)",         border: "1px solid rgba(201,168,76,0.35)" },
  blood:  { background: "var(--blood)",         color: "var(--paper)",        fontWeight: 700 },
  teal:   { background: "var(--doctor-teal)",   color: "var(--paper)",        fontWeight: 700 },
  danger: { background: "#2d0606",              color: "var(--blood-bright)", border: "1px solid var(--blood)" },
  dim:    { background: "rgba(255,255,255,0.06)", color: "rgba(242,234,216,0.5)", border: "1px solid rgba(255,255,255,0.1)" },
};

export function Btn({ variant = "gold", sm, full = true, style, disabled, children, onClick }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "block",
        width: (!sm && full) ? "100%" : "auto",
        padding: sm ? "0.45rem 0.9rem" : "0.85rem 1.5rem",
        fontFamily: "'Courier Prime', monospace",
        fontSize: sm ? "0.68rem" : "0.82rem",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        border: "none",
        borderRadius: 2,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "opacity 0.15s, transform 0.1s",
        opacity: disabled ? 0.38 : 1,
        ...BTN_VARIANTS[variant],
        ...style,
      }}
      onMouseDown={(e) => !disabled && (e.currentTarget.style.transform = "scale(0.97)")}
      onMouseUp={(e)   => !disabled && (e.currentTarget.style.transform = "scale(1)")}
      onMouseLeave={(e)=> (e.currentTarget.style.transform = "scale(1)")}
    >
      {children}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════
// DIVIDER
// ══════════════════════════════════════════════════════════════
export function Divider() {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"1rem", margin:"1.25rem 0", color:"rgba(201,168,76,0.35)", fontSize:"0.7rem", letterSpacing:"0.15em" }}>
      <span style={{ flex:1, height:1, background:"rgba(201,168,76,0.15)", display:"block" }}/>
      or
      <span style={{ flex:1, height:1, background:"rgba(201,168,76,0.15)", display:"block" }}/>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TOAST
// ══════════════════════════════════════════════════════════════
const TOAST_COLORS = {
  error:   { bg:"rgba(74,26,26,0.96)",  border:"#802a2a" },
  success: { bg:"rgba(26,74,26,0.96)",  border:"#2a802a" },
  info:    { bg:"rgba(26,42,74,0.96)",  border:"#2a4080" },
};

export function Toast({ toasts }) {
  return (
    <div style={{ position:"fixed", top:"1.2rem", right:"1.2rem", zIndex:400, display:"flex", flexDirection:"column", gap:"0.4rem", maxWidth:300, width:"calc(100vw - 2.4rem)" }}>
      {toasts.map((t) => {
        const c = TOAST_COLORS[t.type] || TOAST_COLORS.info;
        return (
          <div key={t.id} style={{ background:c.bg, border:`1px solid ${c.border}`, color:"var(--paper)", padding:"0.7rem 1rem", borderRadius:2, fontSize:"0.78rem", animation:"toastIn 0.3s ease" }}>
            {t.msg}
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// CONNECTION STATUS
// ══════════════════════════════════════════════════════════════
export function ConnStatus({ status, code }) {
  const colors = { online:"#4db84d", offline:"#e05555", connecting:"var(--gold)" };
  return (
    <div style={{ position:"fixed", bottom:"0.8rem", left:"0.8rem", fontSize:"0.55rem", letterSpacing:"0.15em", color:"rgba(201,168,76,0.4)", display:"flex", alignItems:"center", gap:"0.4rem", zIndex:50 }}>
      <div style={{ width:6, height:6, borderRadius:"50%", background:colors[status] || colors.connecting, animation:status==="connecting"?"pulse 1s infinite":"none" }}/>
      {status === "online" ? `Live${code ? " · " + code : ""}` : status === "connecting" ? "Connecting..." : "Offline"}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TIMER BAR
// ══════════════════════════════════════════════════════════════
const PHASE_LABELS = { night:"MAFIA PHASE", save:"DOCTOR PHASE", day:"VOTING TIME" };

export function TimerBar({ timerEndsAt, timerDuration, phase }) {
  const dur = timerDuration || VOTE_DURATION;
  const { pct, urgent, active, display } = useTimer(timerEndsAt, dur);
  if (!active) return null;
  return (
    <div style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(201,168,76,0.18)", borderRadius:2, padding:"0.55rem 1rem" }}>
      <div style={{ display:"flex", alignItems:"center", gap:"0.75rem" }}>
        <span style={{ fontSize:"0.58rem", letterSpacing:"0.15em", color:"var(--gold-dim)", whiteSpace:"nowrap" }}>
          {PHASE_LABELS[phase] || "PHASE TIME"}
        </span>
        <div style={{ flex:1, height:5, background:"rgba(255,255,255,0.07)", borderRadius:3, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, background:urgent?"var(--blood-bright)":"var(--gold)", transition:"width 1s linear, background 0.5s", borderRadius:3 }}/>
        </div>
        <span style={{ fontFamily:"'Playfair Display', serif", fontSize:"1.3rem", fontWeight:700, color:urgent?"var(--blood-bright)":"var(--gold)", minWidth:"2.8rem", textAlign:"right" }}>
          {display}
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ANNOUNCEMENT OVERLAY
// ══════════════════════════════════════════════════════════════
const ANN_STYLES = {
  death:           { border:"2px solid var(--blood)",       bg:"linear-gradient(135deg,#130303,#2b0808)" },
  survived:        { border:"2px solid #2a8080",             bg:"linear-gradient(135deg,#03100d,#082222)" },
  "mafia-caught":  { border:"2px solid var(--gold)",         bg:"linear-gradient(135deg,#0b0b03,#1c1c08)" },
  "innocent-killed":{ border:"2px solid var(--blood)",       bg:"linear-gradient(135deg,#130303,#2b0808)" },
  "no-vote":       { border:"2px solid #2a8080",             bg:"linear-gradient(135deg,#03100d,#082222)" },
  tie:             { border:"2px solid #2a8080",             bg:"linear-gradient(135deg,#03100d,#082222)" },
  "mafia-win":     { border:"2px solid var(--blood-bright)", bg:"linear-gradient(135deg,#130303,#2b0808)" },
  "town-win":      { border:"2px solid var(--gold)",         bg:"linear-gradient(135deg,#0b0b03,#1c1c08)" },
};
const ANN_ICONS = {
  death:"💀", survived:"💊", "mafia-caught":"⚖️", "innocent-killed":"💔",
  "no-vote":"🤝", tie:"🤝", "mafia-win":"🔪", "town-win":"🏛️",
};
const ANN_TITLE = {
  death:            (a) => `${a.name} was found dead`,
  survived:         ()  => "The Town is Safe Tonight",
  "mafia-caught":   (a) => `${a.name} eliminated`,
  "innocent-killed":(a) => `${a.name} eliminated`,
  "no-vote":        ()  => "No Consensus",
  tie:              ()  => "It's a Tie!",
  "mafia-win":      ()  => "Mafia Wins",
  "town-win":       ()  => "Town Wins",
};

export function Announcement({ ann, onClose }) {
  if (!ann) return null;
  const s    = ANN_STYLES[ann.type]    || ANN_STYLES.survived;
  const icon = ANN_ICONS[ann.type]     || "📢";
  const title= (ANN_TITLE[ann.type] || (() => ""))(ann);

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, animation:"fadeIn 0.3s ease", padding:"1rem" }}>
      <div style={{ ...s, background:s.bg, textAlign:"center", padding:"clamp(1.5rem, 5vw, 3rem) clamp(1.2rem, 4vw, 2.5rem)", borderRadius:4, maxWidth:420, width:"100%", animation:"cardReveal 0.5s cubic-bezier(0.34,1.56,0.64,1)" }}>
        <div style={{ fontSize:"clamp(2.5rem, 8vw, 4rem)", marginBottom:"0.75rem" }}>{icon}</div>
        <div style={{ fontFamily:"'Playfair Display', serif", fontSize:"clamp(1.2rem, 4vw, 1.6rem)", color:"var(--paper)", marginBottom:"0.5rem" }}>{title}</div>
        <div style={{ fontSize:"0.82rem", color:"rgba(242,234,216,0.6)", lineHeight:1.6, marginBottom:"1rem" }}>{ann.text}</div>
        {ann.type === "death"             && <div style={{ fontSize:"0.9rem", color:"rgba(242,234,216,0.4)", marginBottom:"0.5rem" }}>A Citizen has died — role unknown</div>}
        {ann.type === "mafia-caught"      && <div style={{ fontSize:"0.95rem", color:"#e05555", fontWeight:700, marginBottom:"0.5rem" }}>The last MAFIA is gone! Well done, Town!</div>}
        {ann.type === "innocent-killed"   && <div style={{ fontSize:"0.9rem",  color:"#8090b0",              marginBottom:"0.5rem" }}>An innocent was lost...</div>}
        {ann.type === "town-win"          && <div style={{ fontSize:"0.95rem", color:"var(--gold)",  fontWeight:700 }}>Justice prevails.</div>}
        {ann.type === "mafia-win"         && <div style={{ fontSize:"0.95rem", color:"#e05555",     fontWeight:700 }}>The Mafia reigns supreme.</div>}
        <Btn variant="ghost" style={{ maxWidth:180, margin:"1.2rem auto 0", display:"block" }} onClick={onClose}>Continue</Btn>
      </div>
    </div>
  );
}
